import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import tinycolor from '@ctrl/tinycolor';
import { elements } from './sdb-elements.js';
import '@polymer/iron-image/iron-image.js';
/**
 * Progresscard, used on dashboard (similar to imagecard)
 *
 * @slot left - used for all content on the top left side
 * @slot right - used for all content on the top right side
 * @slot bottom - used for all content in the bottom section (usually list item)
 * @prop {String} img - used to define an image to be used as background in the topsection
 * @prop {String} placeholder - used to define an image to be used as placeholder
 * @prop {String} leftColor - optional, used to set a color of the left side of the overlay (default: #f49d10)
 * @prop {String} rightColor - optional, used to set a color of the right side of the overlay (default: #1f9bb4)
 * @prop {Boolean} noHover - optional, used to remove the hover effect of the entire card
 *
 */
let SDBContentProgressCard = class SDBContentProgressCard extends LitElement {
    constructor() {
        super(...arguments);
        this.img = '';
        this.placeholder = '';
        this.leftColor = '--progress-primary';
        this.rightColor = '--theme-tertiary';
        this.noHover = false;
    }
    _getColor(c) {
        const styles = getComputedStyle(this);
        const bgColor = styles.getPropertyValue(c) || c;
        const tc = tinycolor(bgColor);
        tc.setAlpha(0.7);
        return tc.toRgbString();
    }
    render() {
        return html `
            <style>
                .column50L {
                    background-color: ${this._getColor(this.leftColor)};
                }

                .column50R {
                    background-color: ${this._getColor(this.rightColor)};
                }
            </style>

            <section id="topWrapper">
                <iron-image
                    class="backgroundImage"
                    sizing="cover"
                    fade
                    preload
                    src="${this.img}"
                    placeholder="${this.placeholder}"
                >
                </iron-image>
                <div id="topContent">
                    <div class="column50L">
                        <slot name="left"></slot>
                    </div>
                    <div class="column50R">
                        <slot name="right"></slot>
                    </div>
                </div>
            </section>
            <section id="bottomWrapper">
                <slot name="bottom"></slot>
            </section>
        `;
    }
};
SDBContentProgressCard.styles = [
    elements,
    css `
            :host {
                background: white;
                position: relative;
                height: 100%;
                width: 100%;
                color: var(--text-primary);
                display: grid;
                grid-template-columns: 1fr;
                grid-template-rows: 120px auto; /* rows will use full available height */
                grid-template-areas:
                    'top'
                    'bottom';
                transition: box-shadow 0.15s ease;
            }

            :host(:hover) {
                box-shadow: 0px 4px 14px 1px rgba(0, 0, 0, 0.25);
            }

            :host(:hover),
            :host(:hover) ::slotted(*) {
                cursor: pointer;
            }

            :host(:hover) #bottomWrapper ::slotted(*) {
                background: transparent;
            }

            :host([noHover]:hover) {
                cursor: default;
                -webkit-box-shadow: none;
                box-shadow: none;
                transform: none;
            }

            :host([noHover]:hover) #bottomWrapper ::slotted(*) {
                cursor: default;
            }

            section#topWrapper {
                grid-area: top;
                position: relative;
                display: block;
                width: 100%;
                height: 120px;
                background-color: var(--theme-primary);
                color: var(--text-light);
            }

            section#bottomWrapper {
                grid-area: bottom;
                display: flex;
                flex-flow: column wrap;
                justify-content: stretch;
                align-content: stretch;
                background: white;
                padding: 0;
            }

            div#topContent {
                display: table;
                width: 100%;
                height: 100%;
                position: absolute;
            }

            .column50L,
            .column50R {
                display: table-cell;
                width: 50%;
                text-align: center;
                vertical-align: middle;
                padding: 10px 10px 10px 20px;
            }

            .backgroundImage {
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
                filter: grayscale(100%);
                display: block;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
            }
        `,
];
__decorate([
    property({ type: String })
], SDBContentProgressCard.prototype, "img", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressCard.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressCard.prototype, "leftColor", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressCard.prototype, "rightColor", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressCard.prototype, "noHover", void 0);
SDBContentProgressCard = __decorate([
    customElement('sdb-content-progresscard')
], SDBContentProgressCard);
export { SDBContentProgressCard };
//# sourceMappingURL=sdb-content-progresscard.js.map